/**
 * A seat's ninja on the join screen: the real character, run by its own small simulation, drawn
 * by the silhouette renderer into a little night screen of its own in the seat's headband color, and
 * always running, the way a fighting game's character select keeps its fighters moving. The
 * camera runs with it, so it runs in place while the floor's dashes stream past. Nothing here is
 * game state; the figure is decoration.
 */

import {
  NEON,
  type Pose,
  type PoseName,
  type RenderFeel,
  SilhouetteRenderer,
  ViewState,
} from "@lache/render";
import { type CourseData, createInputFrame, Sim, type Tuning } from "@lache/sim";
import type { Sprite } from "pixi.js";
import { MiniStage } from "./stage";

/** The floor: long enough for a lobby that stays open for hours; the runner wraps back before its end. */
const FLOOR_LENGTH = 100000;
const WRAP_AT = 90000;
const WRAP_BY = 80000;
const STAGE: CourseData = {
  version: 1,
  name: "seat",
  start: { x: 0, y: 0 },
  respawns: [{ x: 0, y: 0 }],
  marks: [],
  entities: [{ type: "solid", x: -20, y: -1, w: FLOOR_LENGTH + 40, h: 1 }],
};

/** The little screen in CSS pixels and the meters across it. */
const CANVAS_W = 224;
const CANVAS_H = 160;
const VIEW_WIDTH = 4.4;
/** The floor sits this far up the screen, the runner this far from the left edge. */
const FLOOR_FRACTION = 0.2;
const RUNNER_FRACTION = 0.42;
/** The floor's dashes: one every this many meters, this long. */
const DASH_EVERY = 1;
const DASH_LENGTH = 0.45;
const DASH_COUNT = 10;
/** The little screen's night, and the floor's neon. */
const NIGHT = 0x14082e;
const FLOOR = NEON.cyan;
const FLOOR_GLOW = NEON.purple;
const DASH = NEON.dim;

export class SeatFigure {
  private readonly stage: MiniStage;
  private readonly sim: Sim;
  private readonly view = new ViewState();
  private readonly frame = createInputFrame();
  private body: SilhouetteRenderer | null = null;
  private shadow: Sprite | null = null;
  private dashes: Sprite[] = [];
  private accent = 0xd0342c;
  private visibleHeight = 1;
  private across = 1;
  private started = false;

  constructor(
    parent: HTMLElement,
    tuning: Tuning,
    private readonly feel: RenderFeel,
    private readonly poses: Record<PoseName, Pose>,
  ) {
    this.sim = new Sim(STAGE, tuning);
    this.frame.moveX = 1;
    this.stage = new MiniStage({
      parent,
      className: "seat-canvas",
      width: CANVAS_W,
      height: CANVAS_H,
      background: NIGHT,
      resolution: 1,
    });
  }

  private build(): void {
    const f = this.stage.shapes;
    const vp = this.stage.viewport;
    vp.setRect(0, 0, CANVAS_W, CANVAS_H, VIEW_WIDTH, this.feel, false);
    this.visibleHeight = CANVAS_H / vp.camera.ppm;
    this.across = CANVAS_W / vp.camera.ppm;
    // The floor is drawn around the camera each frame: a glow, the line, and the dashes that stream past.
    f.rect(vp.world, -20, -0.3, FLOOR_LENGTH + 40, 0.24, FLOOR_GLOW, 0.35);
    f.rect(vp.world, -20, -0.06, FLOOR_LENGTH + 40, 0.06, FLOOR, 0.95);
    for (let i = 0; i < DASH_COUNT; i++)
      this.dashes.push(f.rect(vp.world, 0, -0.16, DASH_LENGTH, 0.07, DASH, 0.6));
    this.shadow = f.disc(vp.world, 0, 0.02, 0.35, this.accent, 0.4);
    this.body = new SilhouetteRenderer(f, this.poses);
    this.body.setAccent(this.accent);
    vp.world.addChild(this.body.root);
  }

  /** The seat's headband changed. */
  setAccent(accent: number): void {
    if (accent === this.accent) return;
    this.accent = accent;
    this.body?.setAccent(accent);
    if (this.shadow) this.shadow.tint = accent;
  }

  /** Tick the simulation up to `nowMs` and draw the frame; call every rendered frame while shown. */
  update(nowMs: number): void {
    if (!this.started) {
      this.started = true;
      void this.stage.init().then(() => this.build());
    }
    if (!this.stage.ready || !this.body) return;
    const { ticks, alpha, dt } = this.stage.clock(nowMs);
    const sim = this.sim;
    for (let i = 0; i < ticks; i++) {
      sim.step(this.frame);
      sim.events.clear();
      // Long before the floor ends, back up by a whole number of dash periods (invisible on this floor).
      if (sim.state.x > WRAP_AT) sim.state.x -= WRAP_BY;
    }
    this.view.update(sim.prev, sim.state, alpha, sim.tuning, this.feel, dt);
    const vp = this.stage.viewport;
    const cam = vp.camera;
    cam.snapTo(
      this.view.x + (0.5 - RUNNER_FRACTION) * this.across,
      (0.5 - FLOOR_FRACTION) * this.visibleHeight,
    );
    vp.applyCamera();
    const first = Math.floor((cam.x - this.across / 2) / DASH_EVERY) * DASH_EVERY;
    this.dashes.forEach((d, i) => {
      d.position.set(first + i * DASH_EVERY, -0.16);
    });
    this.body.draw(this.view, sim, this.feel, dt);
    if (this.shadow) {
      const k = Math.max(0.35, Math.min(1, 1 - this.view.y * 0.22));
      this.shadow.position.set(this.view.x, 0.02);
      this.shadow.width = 0.9 * k;
      this.shadow.height = 0.2 * k;
    }
    this.stage.render();
  }

  /** The scene was hidden: the clock starts over so the ninja does not race to catch up. */
  pause(): void {
    this.stage.resetClock();
  }
}
